import React, { useContext } from 'react';
import Helmet from 'react-helmet';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import { PageContext } from '@/context/PageContext';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import HeroBranded from '@/components/HeroBrandedRebrand/HeroBrandedRebrand';
import styled from 'styled-components';
import { getFeatureGridV1DataFromContentful } from '@/components/FeatureGridV1/contentfulFeatureGridV1';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import { BREAKPOINT } from '@/utils/constants';
import Typography from '@latitude/typography';
import { Link } from '@latitude/link';
import Layout from '@/components/layout';

const PageData = {
  heroBannerData: [
    {
      slides: [
        {
          headingCopy: { content: [], data: {} },
          bodyCopy: { content: [], data: {} },
          heading: 'Digital Card',
          body: 'Manage your card instantly with the Latitude app.',
          image: { file: { url: '/images/mobile-app/hero-digital-card.webp', fileName: 'digital-card' } },
          timer: 10000
        }
      ],
      background: 'white'
    }
  ],
  features: [
    {
      id: 'f1',
      title: 'Instant activation',
      text: 'Activate your card in seconds using the app',
      icon: 'icon-device-mobile-rebrand'
    },
    {
      id: 'f2',
      title: 'Tap & Pay',
      text: 'Add your card to Apple Pay or Google Pay',
      icon: 'icon-wallet-rebrand'
    },
    {
      id: 'f3',
      title: 'Manage payments',
      text: 'View transactions and make payments on the go',
      icon: 'icon-percent-rebrand'
    }
  ],
  faq: [
    { title: 'How do I get the digital card?', content: 'Download the Latitude App and follow the prompts to add your card.' },
    { title: 'Is the digital card secure?', content: 'Yes — the digital card uses tokenisation and device security.' }
  ]
};


const SERVICE_NOTICE = {
  title: 'An update for existing and prospective customers',
  // each inner array is one paragraph; items are separate lines
  paragraphs: [
    [
      'You may have seen recent news about David Jones.',
      'Your David Jones credit card is provided by Latitude Finance Australia, and we want you to know that you can continue to use it for purchases at David Jones and wherever Mastercard is accepted.'
    ],
    [
      'While we’re not currently accepting new applications, this does not affect existing card holders.'
    ]
  ]
};

const NEED_HELP = {
  title: 'Need assistance?',
  intro: 'Our team is here to help.',
  beforeFaq: 'Please check the',
  faqLabel: 'FAQs',
  faqHref: 'https://latitudefs.zendesk.com/hc/en-au/articles/22455005636625-David-Jones-Credit-Cards-FAQs', // change to the id of your FAQ section
  afterFaq: 'and if you still have questions, feel free to contact us on',
  phoneLabel: '1300 550 006',
  phoneHref: 'tel:1300550006',
  hours: 'Monday to Friday, 8:30 AM – 8:00 PM; Saturday, 9:00 AM – 6:00 PM (AEDT).'
};


/* ---------- Page ---------- */

const DavidJonesPage = props => {
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || PageData;
  const featureGridData = getFeatureGridV1DataFromContentful(
    state?.featureSliderData?.[0]
  );

  const getContentfulText = content =>
    content?.content
      ?.flatMap(node => node.content || [])
      ?.map(node => node.value || '')
      ?.join('') || '';

  const transformCardCollectionToRewards = data => {
    const cards = data?.cards || [];

    return cards.map(card => ({
      imageUrl: card?.image?.file?.url ? `https:${card.image.file.url}` : '',
      title: getContentfulText(card?.title),
      description: <span>{getContentfulText(card?.description)}</span>
    }));
  };

  const rewards = transformCardCollectionToRewards(
    state?.cardCollectionData?.[0]
  );

  return (
<Layout location={props.location}>
        <main className="navigation-spacer">
          <Helmet>
            <link
              rel="canonical"
              href={`https://www.latitudefinancial.com.au${PageData.path}`}
            />
            <title>David Jones Credit Card | Latitude Financial</title>
            <meta
              name="description"
              content="Your David Jones credit card continues to work as normal. Find out what's changed and how to get help."
            />
          </Helmet>

          <HeroV2 {...state?.heroBannerData?.[0]} />

          {/* Service notice */}
<StyledNotice>
  <StyledNoticeInner>
    <StyledNoticeTitle>
      <Typography.SH4 color={REBRAND_COLOR.INDIGO}>
        <strong>{SERVICE_NOTICE.title}</strong>
      </Typography.SH4>
    </StyledNoticeTitle>
    <StyledNoticeCopy>
      {SERVICE_NOTICE.paragraphs.map(lines => (
        <Typography.Body key={lines[0]} color={REBRAND_COLOR.INDIGO}>
          {lines.map((line, i) => (
            <React.Fragment key={line}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </Typography.Body>
      ))}
    </StyledNoticeCopy>
  </StyledNoticeInner>
</StyledNotice>

          {/* Need help */}
<StyledHelp>
  <StyledHelpInner>
    <StyledHelpTitle>
      <Typography.H3 color={REBRAND_COLOR.INDIGO}>
        {NEED_HELP.title}
      </Typography.H3>
    </StyledHelpTitle>
    <StyledHelpCopy>
      <Typography.Body color={REBRAND_COLOR.INDIGO}>
        {NEED_HELP.intro}
        <br />
        {NEED_HELP.beforeFaq}{' '}
        <Link
          href={NEED_HELP.faqHref}
          trackEventData={{
            category: 'text-link',
            action: 'in-page-navigation',
            location: 'need-assistance',
            label: NEED_HELP.faqLabel
          }}
        >
          {NEED_HELP.faqLabel}
        </Link>{' '}
        {NEED_HELP.afterFaq}{' '}
        <Link
          href={NEED_HELP.phoneHref}
          trackEventData={{
            category: 'cta-link',
            action: 'phone',
            location: 'need-assistance',
            label: NEED_HELP.phoneLabel
          }}
        >
          {NEED_HELP.phoneLabel}
        </Link>
        ,
        <br />
        {NEED_HELP.hours}
      </Typography.Body>
    </StyledHelpCopy>
  </StyledHelpInner>
</StyledHelp>

          {state?.importantInfoData?.[0] && (
            <ImportantInformation
              id="important-Info"
              css={`
                overflow-x: hidden;
                .row {
                  @media (min-width: 1600px) {
                    padding-left: ${PADDING.P32};
                    padding-right: ${PADDING.P32};
                  }
                }
              `}
              {...{
                ...state?.importantInfoData?.[0],
                sectionOneColummClasses: 'col-12 col-lg-6',
                sectionTwoColummClasses: 'col-12 col-lg-6'
              }}
            />
          )}
        </main>
    </Layout>
   
  );
};

/* ---------- Styles (service notice + need help) ---------- */

const StyledNotice = styled.section`
  background-color: ${REBRAND_COLOR.WHITE};
  padding: 40px 0;

  @media (min-width: ${BREAKPOINT.LG}) {
    padding: 64px 0;
  }
`;


const StyledNoticeTitle = styled.div`
  text-align: center;
  margin-bottom: 24px;

  @media (min-width: ${BREAKPOINT.LG}) {
    margin-bottom: 32px;
  }
`;

const StyledHelpInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
  text-align: center;
`;

const StyledNoticeInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
  text-align: center;
`;

const StyledNoticeCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1100px;
  margin: 0 auto;
`;

const StyledHelp = styled.section`
  background-color: ${REBRAND_COLOR.STONE};
  padding: 40px 0;

  @media (min-width: ${BREAKPOINT.LG}) {
    padding: 64px 0;
  }
`;

const StyledHelpTitle = styled.div`
  margin-bottom: 16px;

  @media (min-width: ${BREAKPOINT.LG}) {
    margin-bottom: 24px;
  }
`;

const StyledHelpCopy = styled.div`
  p {
    margin: 0;
    color: ${REBRAND_COLOR.INDIGO};
  }
`;


export default DavidJonesPage;


